import React, {
  useMemo,
  useCallback,
  useState,
  useRef,
  useEffect
} from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useMediaQuery, useTheme } from '@mui/material';
import { TextField as V4TextField } from '@material-ui/core';
import { MAX_DATE, MIN_DATE } from '~/modules/common/const';
import { mapIsoStringtoUtcObject } from '~/modules/common/dates/convert';
import { useMeContext } from '~/modules/me';
import createCustomAdapterLuxon from './CustomAdapterLuxon';
import DatePickerThemeWrapper from './DatePickerThemeWrapper';

const defaultActions = ['cancel', 'accept'];

const getCalendarIconAriaLabel = (label, value) => {
  const baseText = `Choose ${label || 'date'}`;

  if (value && value.toFormat) {
    const formattedDate = value.toFormat('MMM dd, yyyy');

    return `${baseText}, selected date is ${formattedDate}`;
  }

  return baseText;
};

export const mapLegacyToMUIv5Props = (
  legacyProps,
  setShouldFocusOpenPickerButton,
  openPickerButtonRef
) => {
  // Map v4 view values to v5: 'date' -> 'day'
  const mapViewValue = view => (view === 'date' ? 'day' : view);

  return {
    label: legacyProps.label,
    value: legacyProps.value,
    onChange: () => {}, // Ignore intermediate changes
    onAccept: legacyProps.onChange, // Use onAccept for v3-like behavior
    inputFormat: legacyProps.format || 'yyyy-LL-dd',
    minDate: legacyProps.minDate,
    maxDate: legacyProps.maxDate,
    onOpen: legacyProps.onOpen,
    onClose: (...args) => {
      if (legacyProps.onClose) {
        legacyProps.onClose(...args);
      }

      // FIX ME: Clean this custom focus once all referenced dialog is migrated to muiV5.
      // Use timeout for onClose focus to win the timing race against Dialog focus management
      setTimeout(() => {
        setShouldFocusOpenPickerButton(true);
      }, 100);
    },
    closeOnSelect: legacyProps.autoOk !== false,
    ...(legacyProps.views && { views: legacyProps.views.map(mapViewValue) }),
    ...(legacyProps.openTo && { openTo: mapViewValue(legacyProps.openTo) }),
    disabled: legacyProps.disabled,
    readOnly: legacyProps.readOnly,
    disableFuture: legacyProps.disableFuture,
    disablePast: legacyProps.disablePast,
    shouldDisableDate: legacyProps.shouldDisableDate,
    shouldDisableMonth: legacyProps.shouldDisableMonth,
    shouldDisableYear: legacyProps.shouldDisableYear,
    onError: legacyProps.onError,
    onMonthChange: legacyProps.onMonthChange,
    onViewChange: legacyProps.onViewChange,
    onYearChange: legacyProps.onYearChange,
    showToolbar: legacyProps.showToolbar,
    toolbarTitle: legacyProps.toolbarTitle,
    toolbarFormat: legacyProps.toolbarFormat,
    showDaysOutsideCurrentMonth: legacyProps.showDaysOutsideCurrentMonth,
    disableHighlightToday: legacyProps.disableHighlightToday,
    renderDay: legacyProps.renderDay,
    dayOfWeekFormatter: legacyProps.dayOfWeekFormatter,
    loading: legacyProps.loading,
    renderLoading: legacyProps.renderLoading,
    components: {
      ActionBar: undefined // activates the default action bar
    },
    componentsProps: {
      actionBar: {
        actions: legacyProps.clearable
          ? ['clear', ...defaultActions]
          : defaultActions
      },
      toolbar: legacyProps.ToolbarProps,
      tabs: legacyProps.TabsProps,
      calendarHeader: legacyProps.CalendarHeaderProps
    },
    slotProps: {
      dialog: legacyProps.DialogProps,
      leftArrowButton: legacyProps.leftArrowButtonProps,
      rightArrowButton: legacyProps.rightArrowButtonProps
    },
    OpenPickerButtonProps: {
      ref: openPickerButtonRef,
      'aria-label': getCalendarIconAriaLabel(
        legacyProps.label,
        legacyProps.value
      )
    }
  };
};

export const DatePickerV5 = muiPickerProps => {
  const {
    autoFocus,
    fullWidth,
    inputVariant,
    keyboard,
    views,
    autoFocusOpenPickerButton,
    required,
    classes = {}
  } = muiPickerProps || {};

  const [
    shouldFocusOpenPickerButton,
    setShouldFocusOpenPickerButton
  ] = useState(autoFocusOpenPickerButton || false);

  const openPickerButtonRef = useRef(null);

  const me = useMeContext();
  const locale = me?.locale?.language?.cultureCode;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const keyboardEnabled = isMobile || keyboard;

  const CustomAdapterLuxon = useMemo(() => createCustomAdapterLuxon(me), [me]);

  const minDate = mapIsoStringtoUtcObject(MIN_DATE);

  const maxDateValue = mapIsoStringtoUtcObject(MAX_DATE);
  const maxDate =
    views && views.length === 1 && views.includes('year')
      ? maxDateValue.minus({ year: 1 })
      : maxDateValue;

  useEffect(() => {
    if (shouldFocusOpenPickerButton) {
      openPickerButtonRef?.current?.focus();
      setShouldFocusOpenPickerButton(false);
    }
  }, [shouldFocusOpenPickerButton, openPickerButtonRef]);

  const getInputProps = useCallback(
    params => ({
      ...params.inputProps,
      className: classes.input,
      readOnly: !keyboardEnabled,
      tabIndex: keyboardEnabled ? 0 : -1,
      'aria-label': muiPickerProps['aria-label'],
      'data-qe-id': muiPickerProps['data-qe-id']
    }),
    [classes.input, keyboardEnabled, muiPickerProps]
  );

  const renderInput = useCallback(
    params => {
      return (
        // Using v4 text field to be consistent with other text fields in the app
        <V4TextField
          {...params}
          variant={inputVariant || params.variant}
          fullWidth={fullWidth}
          className={classes.textField}
          autoFocus={autoFocus}
          required={required}
          inputProps={getInputProps(params)}
        />
      );
    },
    [
      inputVariant,
      fullWidth,
      classes.textField,
      autoFocus,
      required,
      getInputProps
    ]
  );

  const v5Props = mapLegacyToMUIv5Props(
    muiPickerProps,
    setShouldFocusOpenPickerButton,
    openPickerButtonRef
  );

  return (
    <DatePickerThemeWrapper>
      <LocalizationProvider
        dateAdapter={CustomAdapterLuxon}
        adapterLocale={locale}
      >
        <DatePicker
          renderInput={renderInput}
          minDate={minDate}
          maxDate={maxDate}
          disableMaskedInput
          {...v5Props}
        />
      </LocalizationProvider>
    </DatePickerThemeWrapper>
  );
};

export default DatePickerV5;
